import { Link } from '@tanstack/react-router'
import { CheckCheck, Clock3, Inbox, Settings2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { describeApiError } from '@/api/problemDetails'
import { Alert, Badge, Button, buttonVariants, EmptyState, Skeleton } from '@/components/ui'
import { useCurrentTenant } from '@/features/tenants/useCurrentTenant'

import { AwaitingYouCard } from './components/AwaitingYouCard'
import { TasksSection } from './components/TasksSection'
import { useTriageOverview } from './queries'
import { formatAge } from './triageFormatting'

/**
 * Pendências (ADR-028): o que espera você (por prioridade e espera), o que espera o contato e as tarefas. Estado
 * vazio = próxima ação: Triagem desligada aponta para Configurações; ligada e sem pendência diz "tudo respondido".
 * Nada aqui cita o provedor de canal nem o modelo de IA (docs/design/00 §5); a prioridade vem do servidor, por código.
 */
export function PendenciasPage() {
  const { t } = useTranslation()
  const { tenant } = useCurrentTenant()
  const overview = useTriageOverview(tenant?.id)

  if (tenant === null) {
    return null
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-foreground-primary">{t('triage.title')}</h1>
        <p className="text-sm text-foreground-muted">{t('triage.subtitle')}</p>
      </header>

      {overview.isPending ? (
        <div
          className="flex flex-col gap-3"
          role="status"
          aria-busy="true"
          aria-label={t('loading')}
        >
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-28 w-full rounded-lg" />
          ))}
        </div>
      ) : overview.isError ? (
        <Alert variant="danger" title={t('triage.loadFailed')}>
          <ul className="list-disc pl-4">
            {describeApiError(overview.error).map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
          <div className="mt-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                void overview.refetch()
              }}
            >
              {t('retry')}
            </Button>
          </div>
        </Alert>
      ) : !overview.data.enabled && overview.data.awaitingYou.length === 0 ? (
        <EmptyState
          icon={Settings2}
          title={t('triage.disabled.title')}
          description={t('triage.disabled.description')}
          action={
            <Link
              to="/app/$tenantSlug/settings"
              params={{ tenantSlug: tenant.slug }}
              className={buttonVariants({ variant: 'primary', size: 'sm' })}
            >
              {t('triage.disabled.action')}
            </Link>
          }
        />
      ) : (
        <>
          {!overview.data.enabled ? (
            <Alert variant="warning" title={t('triage.disabledBanner.title')}>
              <p>{t('triage.disabledBanner.description')}</p>
              <div className="mt-2">
                <Link
                  to="/app/$tenantSlug/settings"
                  params={{ tenantSlug: tenant.slug }}
                  className={buttonVariants({ variant: 'secondary', size: 'sm' })}
                >
                  {t('triage.disabled.action')}
                </Link>
              </div>
            </Alert>
          ) : null}

          <section className="flex flex-col gap-3" aria-labelledby="triage-awaiting-you-title">
            <h2
              id="triage-awaiting-you-title"
              className="inline-flex items-center gap-2 text-base font-semibold text-foreground-primary"
            >
              <Inbox aria-hidden="true" className="h-4 w-4 text-foreground-muted" />
              {t('triage.awaitingYou.title')}
              <Badge variant={overview.data.counts.awaitingYou > 0 ? 'warning' : 'neutral'}>
                {overview.data.counts.awaitingYou}
              </Badge>
            </h2>
            {overview.data.awaitingYou.length === 0 ? (
              <EmptyState
                icon={CheckCheck}
                title={t('triage.awaitingYou.emptyTitle')}
                description={t('triage.awaitingYou.emptyDescription')}
                className="py-8"
              />
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {overview.data.awaitingYou.map((item) => (
                  <AwaitingYouCard
                    key={item.conversationSeq}
                    tenantId={tenant.id}
                    tenantSlug={tenant.slug}
                    item={item}
                  />
                ))}
              </div>
            )}
          </section>

          <section className="flex flex-col gap-3" aria-labelledby="triage-awaiting-them-title">
            <h2
              id="triage-awaiting-them-title"
              className="inline-flex items-center gap-2 text-base font-semibold text-foreground-primary"
            >
              <Clock3 aria-hidden="true" className="h-4 w-4 text-foreground-muted" />
              {t('triage.awaitingThem.title')}
              <Badge variant="neutral">{overview.data.counts.awaitingThem}</Badge>
            </h2>
            {overview.data.awaitingThem.length === 0 ? (
              <p className="text-sm text-foreground-muted">{t('triage.awaitingThem.empty')}</p>
            ) : (
              <ul className="flex flex-col divide-y divide-border-default rounded-lg border border-border-default bg-background-elevated">
                {overview.data.awaitingThem.map((item) => {
                  const age = formatAge(item.lastOutboundAt, t)
                  return (
                    <li
                      key={item.conversationSeq}
                      className="flex items-center justify-between gap-3 px-3 py-2"
                    >
                      <Link
                        to="/app/$tenantSlug/desk"
                        params={{ tenantSlug: tenant.slug }}
                        search={{ conversation: item.conversationSeq }}
                        className="flex min-w-0 flex-col hover:underline"
                      >
                        <span className="truncate text-sm font-medium text-foreground-primary">
                          {item.contactName ?? t('desk.list.unknownContact')}
                        </span>
                        {item.summary !== null ? (
                          <span className="truncate text-xs text-foreground-muted">
                            {item.summary}
                          </span>
                        ) : null}
                      </Link>
                      {age !== null ? (
                        <span className="shrink-0 text-xs text-foreground-muted">
                          {t('triage.awaitingThem.repliedAgo', { time: age })}
                        </span>
                      ) : null}
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          <TasksSection
            tenantId={tenant.id}
            tenantSlug={tenant.slug}
            openTasks={overview.data.tasks}
          />
        </>
      )}
    </div>
  )
}
